import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDate, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InstallationsReport } from '../components/InstallationsReport';
import { DateSearchReport } from '../components/DateSearchReport';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  Download,
  FileText,
  FileSpreadsheet,
  Users,
  PoundSterling,
  Zap,
  Calendar,
  Loader2,
  Filter,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  Clock,
  AlertCircle,
  Package,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

// Status colors for pipeline chart
const STATUS_COLORS = {
  lead: '#3b82f6',
  qualified: '#6366f1',
  quote_sent: '#8b5cf6',
  approved: '#22c55e',
  deposit_taken: '#14b8a6',
  scheduled: '#f59e0b',
  ordered: '#f97316',
  in_progress_installation: '#ef4444',
  in_progress_invoiced: '#ec4899',
  in_progress_handover: '#8b5cf6',
  completed: '#10b981',
};

const STAGE_NAMES = {
  lead: 'Lead',
  qualified: 'Qualified',
  quote_sent: 'Quote Sent',
  approved: 'Approved',
  deposit_taken: 'Deposit Taken',
  scheduled: 'Scheduled',
  ordered: 'Ordered',
  in_progress_installation: 'In Progress (Installation)',
  in_progress_invoiced: 'In Progress (Invoiced)',
  in_progress_handover: 'In Progress (Handover)',
  completed: 'Completed',
};

export const Reporting = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');

  useEffect(() => {
    fetchData();
    fetchUsers();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (assignedFilter !== 'all') params.assigned_to = assignedFilter;

      const [summaryRes, customersRes] = await Promise.all([
        api.get('/reports/summary', { params }),
        api.get('/reports/customers', { params })
      ]);

      setSummary(summaryRes.data);
      setCustomers(customersRes.data.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleExport = async (format, type = 'customers') => {
    setExporting(true);
    try {
      const params = { report_type: type };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await api.get(`/reports/export/${format}`, {
        params,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const applyFilters = () => {
    fetchData();
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatusFilter('all');
    setAssignedFilter('all');
    setTimeout(fetchData, 100);
  };

  // Calculate pipeline data for visualization
  const getPipelineData = () => {
    if (!summary?.status_counts) return [];
    
    return Object.entries(summary.status_counts).map(([status, count]) => ({
      status,
      name: STAGE_NAMES[status] || status,
      count,
      value: summary.revenue_by_status?.[status] || 0,
      color: STATUS_COLORS[status] || '#6b7280'
    })).sort((a, b) => {
      const order = Object.keys(STAGE_NAMES);
      return order.indexOf(a.status) - order.indexOf(b.status);
    });
  };

  const pipelineData = getPipelineData();
  const maxCount = Math.max(...pipelineData.map(d => d.count), 1);

  return (
    <Layout>
      <div className="space-y-6" data-testid="reporting-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Reporting</h1>
              <p className="text-slate-500">Advanced analytics and exports</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => handleExport('csv', 'customers')}
              disabled={exporting}
              data-testid="export-csv-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('pdf', 'summary')}
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Summary PDF
            </Button>
            <Button
              onClick={() => handleExport('pdf', 'features')}
              disabled={exporting}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="export-features-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Feature Summary
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-[160px]"
                  data-testid="start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-[160px]"
                  data-testid="end-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {Object.entries(STAGE_NAMES).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="assigned-filter">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={applyFilters} data-testid="apply-filters-btn">
                  <Filter className="w-4 h-4 mr-2" />
                  Apply
                </Button>
                <Button variant="outline" onClick={clearFilters}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Customers</p>
                      <p className="text-3xl font-bold text-slate-900" data-testid="total-customers">
                        {summary?.total_customers || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  {summary?.conversion_rate > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-sm text-emerald-600">
                      <ArrowUpRight className="w-4 h-4" />
                      <span>{summary.conversion_rate}% conversion rate</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Pipeline Value</p>
                      <p className="text-3xl font-bold text-slate-900" data-testid="pipeline-value">
                        £{(summary?.total_system_value || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <PoundSterling className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                    <TrendingUp className="w-4 h-4" />
                    <span>{summary?.total_system_size?.toFixed(1) || 0} kW total capacity</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Deposits Collected</p>
                      <p className="text-3xl font-bold text-slate-900" data-testid="deposits-collected">
                        £{(summary?.payments?.deposits_total || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                  <div className="text-sm text-slate-500 mt-2">
                    {summary?.payments?.deposits_collected || 0} deposits received
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Installations</p>
                      <p className="text-3xl font-bold text-slate-900" data-testid="installations-count">
                        {summary?.installations_count || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="text-sm text-slate-500 mt-2">
                    Scheduled installations
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Tabs */}
            <Tabs defaultValue="pipeline" className="space-y-4">
              <TabsList>
                <TabsTrigger value="pipeline" data-testid="tab-pipeline">
                  <PieChart className="w-4 h-4 mr-2" />
                  Pipeline
                </TabsTrigger>
                <TabsTrigger value="trends" data-testid="tab-trends">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Trends
                </TabsTrigger>
                <TabsTrigger value="customers" data-testid="tab-customers">
                  <Users className="w-4 h-4 mr-2" />
                  Customer List
                </TabsTrigger>
                <TabsTrigger value="installations" data-testid="tab-installations">
                  <Package className="w-4 h-4 mr-2" />
                  Installations
                </TabsTrigger>
                <TabsTrigger value="date-search" data-testid="tab-date-search">
                  <Calendar className="w-4 h-4 mr-2" />
                  Date Search
                </TabsTrigger>
              </TabsList>

              {/* Pipeline Tab */}
              <TabsContent value="pipeline">
                <Card>
                  <CardHeader>
                    <CardTitle>Sales Pipeline</CardTitle>
                    <CardDescription>Customer distribution across pipeline stages</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {pipelineData.map((stage) => (
                        <div key={stage.status} className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: stage.color }}
                              />
                              <span className="font-medium">{stage.name}</span>
                            </div>
                            <div className="flex items-center gap-4 text-slate-500">
                              <span>{stage.count} customers</span>
                              <span className="font-medium text-slate-900">
                                £{stage.value.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <div className="h-8 bg-slate-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full rounded-lg transition-all duration-500"
                              style={{
                                width: `${(stage.count / maxCount) * 100}%`,
                                backgroundColor: stage.color
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pipeline Summary */}
                    <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {summary?.status_counts?.lead || 0}
                        </p>
                        <p className="text-sm text-slate-500">Active Leads</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-600">
                          {(summary?.status_counts?.scheduled || 0) + 
                           (summary?.status_counts?.ordered || 0) +
                           (summary?.status_counts?.in_progress_installation || 0)}
                        </p>
                        <p className="text-sm text-slate-500">In Progress</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-emerald-600">
                          {summary?.status_counts?.completed || 0}
                        </p>
                        <p className="text-sm text-slate-500">Completed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Trends Tab */}
              <TabsContent value="trends">
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Trends</CardTitle>
                    <CardDescription>Customer acquisition and revenue over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {summary?.monthly_trends?.length > 0 ? (
                      <div className="space-y-6">
                        {/* Simple bar chart for monthly data */}
                        <div className="h-64 flex items-end gap-2">
                          {summary.monthly_trends.map((month, idx) => {
                            const maxValue = Math.max(...summary.monthly_trends.map(m => m.value), 1);
                            const height = (month.value / maxValue) * 100;
                            return (
                              <div key={month.month} className="flex-1 flex flex-col items-center gap-2">
                                <div className="text-xs text-slate-500 font-medium">
                                  £{(month.value / 1000).toFixed(0)}k
                                </div>
                                <div 
                                  className="w-full bg-gradient-to-t from-indigo-500 to-purple-500 rounded-t-lg transition-all duration-500"
                                  style={{ height: `${Math.max(height, 5)}%` }}
                                />
                                <div className="text-xs text-slate-500">
                                  {month.month.slice(5)}
                                </div>
                                <div className="text-xs font-medium text-slate-700">
                                  {month.count}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Legend */}
                        <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-gradient-to-r from-indigo-500 to-purple-500" />
                            <span>Revenue</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-700 font-medium">Numbers</span>
                            <span>= Customer Count</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-500">
                        <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No trend data available for the selected period</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Customers Tab */}
              <TabsContent value="customers">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Customer List</CardTitle>
                      <CardDescription>
                        {customers.length} customers matching filters
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('csv', 'customers')}
                        disabled={exporting}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        CSV
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('pdf', 'customers')}
                        disabled={exporting}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>System</TableHead>
                          <TableHead>Value</TableHead>
                          <TableHead>Assigned</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customers.slice(0, 50).map((customer) => (
                          <TableRow key={customer.id}>
                            <TableCell className="font-medium">{customer.name}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p>{customer.email}</p>
                                <p className="text-slate-500">{customer.phone}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                className="text-xs"
                                style={{ 
                                  backgroundColor: STATUS_COLORS[customer.status] + '20',
                                  color: STATUS_COLORS[customer.status]
                                }}
                              >
                                {customer.status_display}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Zap className="w-3 h-3 text-amber-500" />
                                {customer.system_size} kW
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              £{customer.system_cost.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {customer.assigned_to}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {formatDate(customer.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {customers.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No customers match the selected filters</p>
                      </div>
                    )}
                    {customers.length > 50 && (
                      <div className="p-4 text-center text-sm text-slate-500 border-t">
                        Showing 50 of {customers.length} customers. Export to see all.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Installations Tab */}
              <TabsContent value="installations">
                <InstallationsReport />
              </TabsContent>

              {/* Date Search Tab */}
              <TabsContent value="date-search">
                <DateSearchReport />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
};
